import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addVehicleProperty } from '../../../../actions/AllUpdates';
import _ from 'lodash';
import domtoimage from 'dom-to-image';
import jsPDF from 'jspdf';
import axios from 'axios';

const DviTab = () => {
  const dispatch = useDispatch();
  const active_vehicle = useSelector((state) => state.settings_refresh.active_vehicle);
  const installation = useSelector((state) => state.settings_dealerships.installation);
  const current_dealership = useSelector((state) => state.settings_dealerships.current_dealership);
  const siteid = `${current_dealership}_${active_vehicle.id}`;

  const [addPartOrderError, setAddPartOrderError] = useState('');
  const [pdfStatus, setPdfStatus] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [proxiedThumbnailUrl, setProxiedThumbnailUrl] = useState('');
  const logoRef = useRef(null);
  const thumbnailRef = useRef(null);

  // Placeholder images for fallback
  const placeholderLogo = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
  const placeholderThumbnail = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

  // Get original thumbnail SRC
  const originalThumbnailSrc = _.find(active_vehicle?.vehicle_properties, { key: 'thumbnail' })?.value || placeholderThumbnail;

  // Convert DOM-rendered image to Data URL
  const getImageDataURL = (imgElement, placeholder) => {
    return new Promise((resolve) => {
      if (!imgElement || !imgElement.complete || imgElement.naturalWidth === 0) {
        console.warn('Image not loaded or invalid:', imgElement?.src);
        return resolve(placeholder);
      }
      try {
        const canvas = document.createElement('canvas');
        canvas.width = imgElement.naturalWidth;
        canvas.height = imgElement.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imgElement, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        console.log('Generated Data URL:', { src: imgElement.src, dataUrl: dataUrl.substring(0, 50) });
        resolve(dataUrl);
      } catch (error) {
        console.error('Failed to convert image to Data URL:', error);
        resolve(placeholder);
      }
    });
  };

  // Function called when images are ready (now just sets loaded state)
  const imagesReady = () => {
    console.log('Images ready');
    setImagesLoaded(true);
  };

  useEffect(() => {
    if (originalThumbnailSrc !== placeholderThumbnail && !proxiedThumbnailUrl && installation.logo) {
      try {
        const logoUrlObj = new URL(installation.logo);
        // Note: siteid is already defined at top, no need to parse from logo

        axios.post('https://gotoreconfiles.com/cors_new_server.php', {
          action: 'upload-from-url',
          url: originalThumbnailSrc,
          siteid: siteid
        }, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }).then((response) => {
          setProxiedThumbnailUrl(response.data);
          console.log('Proxied thumbnail URL:', response.data);
        }).catch((error) => {
          console.error('Failed to get proxied thumbnail URL:', error);
          // Fallback to placeholder or original if needed
        });
      } catch (error) {
        console.error('Error parsing logo URL for siteid:', error);
      }
    }
  }, [originalThumbnailSrc, proxiedThumbnailUrl, installation.logo, siteid]);

  useEffect(() => {
    console.log('useEffect fired', { deps: { logo: installation.logo, vehicleProps: active_vehicle?.vehicle_properties } });
    const timeout = setTimeout(() => {
      if (!imagesLoaded) {
        console.warn('Image loading timed out, using placeholders');
        setImagesLoaded(true);
      }
    }, 10000);

    if (logoRef.current && thumbnailRef.current) {
      const logoImg = logoRef.current;
      const thumbnailImg = thumbnailRef.current;

      let logoLoaded = logoImg.complete && logoImg.naturalWidth !== 0;
      let thumbnailLoaded = thumbnailImg.complete && thumbnailImg.naturalWidth !== 0;

      const checkImagesLoaded = () => {
        console.log('checkImagesLoaded', { logoLoaded, thumbnailLoaded });
        if (logoLoaded && thumbnailLoaded) {
          imagesReady();
        }
      };

      const handleLogoLoad = () => {
        console.log('Logo image loaded', { src: logoImg.src });
        logoLoaded = true;
        checkImagesLoaded();
      };
      const handleLogoError = () => {
        console.warn('Logo image failed to load', { src: logoImg.src });
        logoLoaded = true; // Treat as "loaded" to proceed with placeholder
        checkImagesLoaded();
      };

      const handleThumbnailLoad = () => {
        console.log('Thumbnail image loaded', { src: thumbnailImg.src });
        thumbnailLoaded = true;
        checkImagesLoaded();
      };
      const handleThumbnailError = () => {
        console.warn('Thumbnail image failed to load', { src: thumbnailImg.src });
        thumbnailLoaded = true;
        checkImagesLoaded();
      };

      logoImg.addEventListener('load', handleLogoLoad);
      logoImg.addEventListener('error', handleLogoError);
      thumbnailImg.addEventListener('load', handleThumbnailLoad);
      thumbnailImg.addEventListener('error', handleThumbnailError);

      checkImagesLoaded(); // Initial check

      return () => {
        logoImg.removeEventListener('load', handleLogoLoad);
        logoImg.removeEventListener('error', handleLogoError);
        thumbnailImg.removeEventListener('load', handleThumbnailLoad);
        thumbnailImg.removeEventListener('error', handleThumbnailError);
        clearTimeout(timeout);
      };
    } else {
      console.warn('Image refs not set, using placeholders');
      setImagesLoaded(true);
    }
  }, [installation.logo, active_vehicle?.vehicle_properties]);

  const onPrint = async () => {
    setPdfStatus('generating');
    const vehicle = active_vehicle;
    const print_page = document.getElementById(`dvi_${vehicle.stock}`);

    // Generate fresh Data URLs from hidden preloaders
    const logoDataUrl = await getImageDataURL(logoRef.current, placeholderLogo);
    const thumbnailDataUrl = await getImageDataURL(thumbnailRef.current, placeholderThumbnail);

    // Temporarily replace image sources with fresh Data URLs
    const logoImg = document.getElementById(`logo_${vehicle.stock}`);
    const thumbnailImg = document.getElementById(`thumbnail_${vehicle.stock}`);
    const originalLogoSrc = logoImg.src;
    const originalThumbnailSrc = thumbnailImg.src;
    logoImg.src = logoDataUrl;
    thumbnailImg.src = thumbnailDataUrl;
    console.log('Original sources:', originalLogoSrc, originalThumbnailSrc);

    // Wait for images to load the new data URLs (should be instant for data URLs, but ensure)
    const loadPromise = (img) => new Promise((resolve) => {
      if (img.complete && img.naturalWidth !== 0) {
        resolve();
      } else {
        img.onload = resolve;
        img.onerror = resolve; // Proceed even on error
      }
    });
    await Promise.all([loadPromise(logoImg), loadPromise(thumbnailImg)]);

    // Small delay to ensure rendering
    await new Promise(resolve => setTimeout(resolve, 50));

    // Temporarily hide PDF status and button
    const pdfStatusElement = print_page.querySelector('.pdf-status');
    const printButton = print_page.querySelector('.print-button');
    const originalStatusDisplay = pdfStatusElement.style.display;
    const originalButtonDisplay = printButton.style.display;
    pdfStatusElement.style.display = 'none';
    printButton.style.display = 'none';

    // Temporarily remove margin for PDF to prevent shift
    const originalMargin = print_page.style.margin;
    print_page.style.margin = '0';

    // Temporarily style for PDF to match A4 dimensions
    const originalWidth = print_page.style.width;
    const originalMinHeight = print_page.style.minHeight;
    const originalPadding = print_page.style.padding;
    const originalBackground = print_page.style.background;
    const originalBoxSizing = print_page.style.boxSizing;
    print_page.style.width = '794px'; // 210mm at 96 DPI
    print_page.style.minHeight = '1123px'; // 297mm at 96 DPI
    print_page.style.padding = '38px'; // 10mm at 96 DPI
    print_page.style.background = '#fff';
    print_page.style.boxSizing = 'border-box';

    try {
      const dataUrl = await domtoimage.toPng(print_page, { 
        quality: 1, 
        bgcolor: '#fff',
        width: 794, // 210mm at 96 DPI
        height: Math.max(print_page.scrollHeight, 1123), // Ensure full content height
        cacheBust: true // Prevent image caching issues
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((resolve) => { img.onload = resolve; });

      const imgWidth = img.width;
      const imgHeight = img.height;
      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 10;
      const maxWidth = pdfWidth - 2 * margin;
      const maxHeight = pdfHeight - 2 * margin;

      // Scale to fill A4 page width
      let width = maxWidth;
      let height = (width / imgWidth) * imgHeight;

      // Adjust height to fit page if necessary
      if (height > maxHeight) {
        height = maxHeight;
        width = (height / imgHeight) * imgWidth;
      }

      const x = margin;
      const y = margin;

      console.log('PDF dimensions:', { imgWidth, imgHeight, widthInMm: width, heightInMm: height, x, y });

      pdf.addImage(dataUrl, 'PNG', x, y, width, height);
      pdf.save(`dvi_${vehicle.stock}.pdf`);
      setPdfStatus('success');
      setTimeout(() => setPdfStatus(null), 3000);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      setPdfStatus('error');
      setTimeout(() => setPdfStatus(null), 5000);
    } finally {
      // Restore original image sources and styles
      logoImg.src = originalLogoSrc;
      thumbnailImg.src = originalThumbnailSrc;
      pdfStatusElement.style.display = originalStatusDisplay;
      printButton.style.display = originalButtonDisplay;
      print_page.style.margin = originalMargin;
      print_page.style.width = originalWidth;
      print_page.style.minHeight = originalMinHeight;
      print_page.style.padding = originalPadding;
      print_page.style.background = originalBackground;
      print_page.style.boxSizing = originalBoxSizing;
    }
  };

  const vehicle = active_vehicle || {};
  const vehicle_properties = vehicle.vehicle_properties || [];
  const appraisals = vehicle.appraisals || [];
  const approvedAppraisals = appraisals.filter(appraisal =>
    ['approved', 'completed'].includes(appraisal.ro_status)
  );
  const totalServicingCost = approvedAppraisals.reduce((sum, appraisal) => {
    const calculated = Number(appraisal.parts_price) + (Number(appraisal.labor_units) * Number(appraisal.labor_price));
    const stored = Number(appraisal.estimate_total) || 0;
    const counter = Number(appraisal.counter_estimate) || 0;
    if (appraisal.ro_status === 'declined' && counter > 0) {
      return sum + counter;
    }
    return sum + (stored > 0 ? stored : calculated);
  }, 0);

  if (!vehicle.vin) {
    console.warn('Missing vehicle.vin, showing loading state');
    return <div>Loading vehicle data...</div>;
  }

  return (
    <div className="bg-default" style={{ padding: '38px' }}>
      {/* Always render hidden preloaders so refs are available and images can load/error */}
      <img
        ref={logoRef}
        src={installation.logo || placeholderLogo}
        alt="Hidden Logo Loader"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
      />
      <img
        ref={thumbnailRef}
        src={proxiedThumbnailUrl || originalThumbnailSrc}  // Use proxied for CORS-safe loading
        alt="Hidden Thumbnail Loader"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
      />
      {imagesLoaded ? (
        <div className="bg-white" id={`dvi_${vehicle.stock}`} style={{ width: '794px', margin: '0 auto', boxSizing: 'border-box', padding: '8px' }}>
          <div className="flex justify-between items-center" style={{ display: 'block', clear: 'both', height: '160px' }}>
            <div>
              <img
                src={originalThumbnailSrc}  // Visible uses original (loads for display)
                alt="Vehicle Thumbnail"
                id={`thumbnail_${vehicle.stock}`}
                className={'float-end'}
                style={{ maxWidth: '200px', marginRight: 0, display: 'block', clear: 'both' }}
              />
            </div>
            <div>
              <img
                src={installation.logo || placeholderLogo}
                alt="Company Logo"
                id={`logo_${vehicle.stock}`}
                style={{ maxWidth: '150px', marginBottom: '10px' }}
              />
              <h3>{installation.softname}</h3>
              <ul className="list-style-none space-left-0 pad-left-0">
                <li>{installation.phone}</li>
                <li>{installation.street1}</li>
                <li>{installation.city}, {installation.state} {installation.zip}</li>
              </ul>
            </div>
          </div>
          <div style={{ width: '100%' }} className="space-top-1">
            <h4 className="text-center">
              Pre-Owned {vehicle.year || 'N/A'} {vehicle.make || 'N/A'} {vehicle.model || 'N/A'} ({vehicle.miles || 'N/A'} Miles)
            </h4>
            <table className="table table-bordered mx-auto" style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td className="font-bold"><span className="text-primary">VIN:</span> {vehicle.vin}</td>
                  <td className="font-bold"><span className="text-primary">Stock #:</span> {vehicle.stock}</td>
                </tr>
                <tr>
                  <td className="font-bold"><span className="text-primary">Trim:</span> {vehicle.body || 'N/A'}</td>
                  <td className="font-bold"><span className="text-primary">Drivetrain:</span> {_.find(vehicle_properties, { key: 'drivetrain' })?.value || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-bold"><span className="text-primary">Exterior:</span> {vehicle.color || 'N/A'}</td>
                  <td className="font-bold"><span className="text-primary">Interior:</span> {_.find(vehicle_properties, { key: 'interior_color' })?.value || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-8 space-top-1">
            <table className="table table-bordered w-full">
              <thead>
                <tr>
                  <th scope="col" style={{ width: '40%', backgroundColor: '#00acd8', color: '#FFF' }}>Work Item</th>
                  <th scope="col" style={{ width: '60%', backgroundColor: '#00acd8', color: '#FFF' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {approvedAppraisals.length > 0 ? (
                  approvedAppraisals.map((appraisal, index) => (
                    <tr key={index}>
                      <td>{appraisal.label_item_1 || 'N/A'}</td>
                      <td>{appraisal.description || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2">No work items available</td>
                  </tr>
                )}
              </tbody>
            </table>
            <h6 className="mt-4 font-bold">Total Servicing Cost: ${totalServicingCost.toFixed(2)}</h6>
          </div>
          <div className="mt-8">
            <p>Don't make an expensive mistake by buying without knowing the full history. Instead buy with confidence with verified service work. Check the mechanical condition of this vehicle by reviewing the receipt of the reconditioning service work that was completed by the dealer.</p>
            <p>Make sure that no matter where you're shopping, to ask the dealer to provide you the iPacket® with iPacket Recon™ receipts.</p>
          </div>
          <div className="mt-4">
            <p className={`pdf-status ${pdfStatus === 'generating' ? 'text-blue-500' : pdfStatus === 'success' ? 'text-green-500' : 'text-red-500'}`}>
              {pdfStatus === 'generating' && 'Generating PDF...'}
              {pdfStatus === 'success' && 'PDF generated successfully!'}
              {pdfStatus === 'error' && 'Failed to generate PDF. Please try again.'}
            </p>
            <button className="btn btn-primary float-right print-button" onClick={onPrint}>Print PDF</button>
          </div>
        </div>
      ) : (
        <div>Loading images</div>
      )}
    </div>
  );
};

export default DviTab;